import "./abModal.style.scss";
import {ESCLASS} from "@/scripts/constants/css.classes";
import AbModalService from "@/scripts/modules/ab-modal/ab-modal.service";
import {AbModalAttributes, AbModalSizingClassesEnum} from "@/scripts/modules/ab-modal/ab-modal.types";
import Functions from "@/scripts/modules/ab-modal/component/abModalFunctions";
import {NAMES} from "@/scripts/modules/ab-modal/abModal.constants";

export class AbModalElement extends HTMLElement {

  private _modalService = AbModalService;
  private config:  AbModalAttributes;

  #initialized: boolean = false;

  constructor() {
    super();
  }

  static get observedAttributes(): string[] { return []; }

  connectedCallback() {
    if(this.#initialized) return;
    this.#initialized = true;
    this.init();
    this.bindEvents();
  }

  disconnectedCallback() {
    this.unbindEvents();
    this._modalService.notifyModalClosed(this.id);
  }

  // Public APIs

  public setTitle (title: string) {
    const headerTitleElement = this.querySelector('.ab-modal__header-title');
    if (!headerTitleElement) {
      console.warn("Cannot set title, header title element not found");
      return;
    }
    headerTitleElement.innerHTML = title;
  }

  public setSizingClass(sizingClass: AbModalSizingClassesEnum) {
    const modalClasses = this.classList.value;
    this.classList.value = modalClasses
      .split(' ')
      .filter(c => !Object.values(AbModalSizingClassesEnum).includes(c as AbModalSizingClassesEnum))
      .join(' ')
      .concat(` ${sizingClass}`);
  }

  // Event Handlers

  private clickEventHandler: (event: MouseEvent) => void;
  private keydownEventHandler: (event: KeyboardEvent) => void;

  // Private Methods

  private init() {
    this.config = Functions.parseConfig(this.getAttribute('attributes'));
    const modalContent = document.createDocumentFragment();
    while (this.firstChild) {
      modalContent.appendChild(this.firstChild);
    }
    this.setAttribute('tabindex', '0');
    this.classList.value = `${NAMES.CLASSES.ROOT} ${this.config?.classesModal ?? ''} ${ESCLASS}`;
    const innerDom = Functions.buildInnerDom(this.config, modalContent);
    this.replaceChildren(innerDom);
    this._modalService.notifyModalOpened(this.id);
    this.focus();
  }

  private bindEvents() {
    this.clickEventHandler = (event: MouseEvent) => this.onClickOnDocument(event);
    this.keydownEventHandler = (event: KeyboardEvent) => this.onKeydown(event);
    document.addEventListener('click', this.clickEventHandler);
    document.addEventListener('keydown', this.keydownEventHandler);
  }

  private unbindEvents() {
    document.removeEventListener('click', this.clickEventHandler);
    document.removeEventListener('keydown', this.keydownEventHandler);
  }

  private onClickOnDocument(event: MouseEvent) {
    const clickedElement = event.target as HTMLElement;
    if (!this.contains(clickedElement)) return;
    // Click on close, close modal
    if (clickedElement.closest(`.${NAMES.CLASSES.CLOSE}`)) {
      this.remove();
      return;
    }
    // Click inside, do nothing
    if (clickedElement.closest(`.${NAMES.CLASSES.WRAPPER}`)) return;
    // Click on backdrop, close modal
    this.remove();
  }

  private onKeydown(event: KeyboardEvent) {
    if (event.key !== 'Escape') return;
    const elementOnFocus = document.activeElement;
    const relatedScope = elementOnFocus?.closest(`.${ESCLASS}`);
    if (!relatedScope || relatedScope !== this) return;
    this.remove();
  }

}

export function defineAbModalElement() {
  const TAG_NAME = NAMES.TAG;
  if (customElements.get(TAG_NAME)) return;
  customElements.define(TAG_NAME, AbModalElement);
}
